import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { createClient } from '@kairos/supabase/browser'

import {
  ErrorBanner,
  Field,
  Stack,
  StatusCard,
  SubmitButton,
} from '~/components/auth/auth-ui'
import { logger } from '~/lib/logger'
import { router } from '~/router'

/**
 * `/authentication/mfa` — CASA 3.3.1 TOTP challenge + enrolment for platform admins.
 *
 * The `/admin` route guard and the admin API both require `aal2`, so an admin lands here
 * when the second factor is missing or unsatisfied. Two stages:
 *   - `enroll`    — no verified TOTP factor yet: show the QR + manual key, then verify.
 *   - `challenge` — a verified factor exists: ask for the current code.
 * On success the router is invalidated so the `/admin` guard re-runs against `aal2`.
 */

export const Route = createFileRoute('/authentication/mfa')({
  component: MfaPage,
})

type Stage =
  | { kind: 'loading' }
  | { kind: 'enroll'; factorId: string; qr: string; secret: string }
  | { kind: 'challenge'; factorId: string }
  | { kind: 'error'; message: string }

function MfaPage(): ReactNode {
  const [stage, setStage] = useState<Stage>({ kind: 'loading' })
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [bannerError, setBannerError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const supabase = createClient()
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData.session) {
        void router.navigate({ to: '/authentication/login' })
        return
      }

      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors()
      if (listError) {
        if (!cancelled) {
          setStage({
            kind: 'error',
            message: 'We could not load your security settings. Try again.',
          })
        }
        return
      }

      const verified = factors?.totp?.find((factor) => factor.status === 'verified')
      if (verified) {
        if (!cancelled) setStage({ kind: 'challenge', factorId: verified.id })
        return
      }

      const { data: enrolled, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      })
      if (enrollError || !enrolled) {
        if (!cancelled) {
          setStage({
            kind: 'error',
            message: 'We could not start two-factor setup. Try again.',
          })
        }
        return
      }
      if (!cancelled) {
        setStage({
          kind: 'enroll',
          factorId: enrolled.id,
          qr: enrolled.totp.qr_code,
          secret: enrolled.totp.secret,
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const submit = useCallback(async (): Promise<void> => {
    if (stage.kind !== 'enroll' && stage.kind !== 'challenge') return
    setBusy(true)
    setBannerError(null)
    try {
      const supabase = createClient()
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: stage.factorId,
      })
      if (challengeError || !challenge) {
        setBannerError('We could not start the verification. Try again.')
        return
      }
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: stage.factorId,
        challengeId: challenge.id,
        code,
      })
      if (verifyError) {
        setBannerError('That code did not work. Check your authenticator app and try again.')
        return
      }
      await router.invalidate()
      void router.navigate({ to: '/admin' })
    } catch (cause: unknown) {
      logger.warn('mfa verification failed', {
        message: cause instanceof Error ? cause.message : 'unknown',
      })
      setBannerError('Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }, [stage, code])

  if (stage.kind === 'loading') {
    return (
      <Stack>
        <p className="ka-auth-sub">Checking your security settings…</p>
      </Stack>
    )
  }

  if (stage.kind === 'error') {
    return (
      <StatusCard
        tone="destructive"
        glyph="!"
        title="Two-factor setup is unavailable"
        body={stage.message}
      />
    )
  }

  const enrolling = stage.kind === 'enroll'

  return (
    <Stack>
      <header>
        <h1 className="ka-auth-h1">
          {enrolling ? 'Set up two-factor authentication' : 'Two-factor verification'}
        </h1>
        <p className="ka-auth-sub">
          {enrolling
            ? 'Scan the code with your authenticator app, then enter the 6-digit code it shows.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </p>
      </header>

      {bannerError ? <ErrorBanner message={bannerError} /> : null}

      {enrolling ? (
        <div className="ka-stack">
          <img
            src={stage.qr}
            alt="Two-factor authentication QR code"
            width={200}
            height={200}
          />
          <p className="ka-field-helper">
            Can&rsquo;t scan it? Enter this key manually: <code>{stage.secret}</code>
          </p>
        </div>
      ) : null}

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
        className="ka-stack"
      >
        <Field
          id="mfa-code"
          label="Authentication code"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <SubmitButton loading={busy} label="Verify" loadingLabel="Verifying…" />
      </form>
    </Stack>
  )
}
